import ClearIcon from '@mui/icons-material/Clear';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import {
  Button,
  Checkbox,
  Collapse,
  Container,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Modal,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectAllCatalogs } from '../../redux/reducers/catalogsReducer';
import { createCustomFramework } from '../../redux/reducers/frameworkReducer';
import ControlGroup from '../ControlGroup';

const Types = Object.freeze({
  GROUP: 'group',
  CONTROL: 'control',
});

function FrameworkList({ catalogList, checkedControls, openControl, handleCheckChange, handleControlOpen }) {
  return (
    <List dense>
      {catalogList.map((catalog) => {
        const catalogPrefix = `${catalog.info.type}:${catalog.info.version}`;
        return (
          <List dense key={`${catalogPrefix}-list`}>
            <ListItem key={catalogPrefix}>
              <ListItemIcon>
                <Checkbox
                  id={`${catalogPrefix}-checkbox`}
                  checked={checkedControls[catalogPrefix].value}
                  onChange={() => handleCheckChange(catalogPrefix)}
                />
              </ListItemIcon>
              <ListItemButton id={catalogPrefix} onClick={() => handleControlOpen(catalogPrefix)}>
                <ListItemText primary={catalog.info.title} />
                {openControl === catalogPrefix ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openControl.startsWith(catalogPrefix)} timeout="auto" key={`${catalogPrefix}-collapse`}>
              <List dense key={`${catalogPrefix}-grouplist`}>
                {catalog.groups.map((group) => (
                  <ControlGroup
                    group={group}
                    checkedControls={checkedControls}
                    openControl={openControl}
                    handleCheckChange={handleCheckChange}
                    handleOpen={handleControlOpen}
                    key={`${group.id}-controlgroup`}
                  />
                ))}
              </List>
            </Collapse>
          </List>
        );
      })}
    </List>
  );
}

export function Details({ initDetails, handleChange }) {
  const { t } = useTranslation();
  const [details, setDetails] = useState(initDetails);

  const handleLocalChange = (event) => {
    const { name, value } = event.target;
    const newDetails = { ...details };
    newDetails[name] = value;
    setDetails(newDetails);
  };

  return (
    <Stack spacing={2}>
      <TextField
        required
        variant="outlined"
        subtype="documentField"
        name="title"
        id="title"
        InputLabelProps={{
          shrink: true,
        }}
        label={t('framework.createModal.labelName')}
        value={details.title}
        onChange={(e) => handleLocalChange(e)}
        onBlur={() => handleChange(details)}
      />
      <TextField
        variant="outlined"
        subtype="documentField"
        name="description"
        id="description"
        multiline
        minRows="4"
        InputLabelProps={{
          shrink: true,
        }}
        label={t('framework.createModal.labelDescription')}
        value={details.description}
        onChange={(e) => handleLocalChange(e)}
        onBlur={() => handleChange(details)}
      />
    </Stack>
  );
}

export function FrameworkCompositionModal({ handleClose }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const initialDetails = { title: '', description: '' };
  const [initialized, setInitialized] = useState(true);
  const [details, setDetails] = useState(initialDetails);
  const catalogList = useSelector(selectAllCatalogs, shallowEqual);
  const initializeChecks = useCallback(() => {
    function parse(content, resultant) {
      let newResult = resultant;
      if (content.id !== undefined) {
        newResult[content.id] = { type: Types.GROUP, value: false };
      }
      if (content.groups) {
        content.groups.forEach((group) => {
          newResult = parse(group, newResult);
        });
      }
      if (content.controls) {
        content.controls.forEach((control) => {
          newResult[control.id] = { type: Types.CONTROL, value: false };
        });
      }
      return newResult;
    }

    let initState = {};
    catalogList.forEach((catalog) => {
      initState[`${catalog.info.type}:${catalog.info.version}`] = { type: Types.GROUP, value: false };
      initState = parse(catalog, initState);
    });
    return initState;
  }, [catalogList]);

  const initialState = useMemo(() => initializeChecks(), [catalogList]);
  const [checkedControls, setCheckedControls] = useState(initialState);
  const [openControl, setOpenControl] = useState('');

  const handleCheckChange = (id) => {
    const newCheckedControls = { ...checkedControls };
    const newValue = !checkedControls[id].value;
    // remove false positives, e.g. deselecting 5.1 should deselect 5.1.1 but not 5.10, but selecting the whole
    // management ISO27001 2022 should also select the base groups (i.e. ISO27001:2022:4)
    const controlsToChange = Object.keys(newCheckedControls)
      .filter((key) => key.startsWith(id) && (key.includes(':', id.length) || key.includes('.', id.length) || key === id));
    // If checking a control or a group into the framework, ensure the levels above are added as well
    if (newValue) {
      const splitId = id.split(':');
      if (splitId.length === 3) {
        const prefix = splitId.slice(0, -1).join(':');
        controlsToChange.push(prefix);
        const splitGroups = splitId.slice(-1)[0].split('.').slice(0, -1);
        splitGroups.forEach((_, index) => {
          controlsToChange.push(`${prefix}:${splitGroups.slice(0, index + 1).join('.')}`);
        });
      }
    }
    controlsToChange.forEach((controlId) => {
      newCheckedControls[controlId].value = newValue;
    });
    setCheckedControls(newCheckedControls);
  };

  const handleControlOpen = (id) => {
    if (id === openControl) {
      const splitId = id.split(':');
      const splitGroups = splitId.length === 3 ? splitId.slice(-1)[0].split('.') : [];
      switch (splitGroups.length) {
        case 0: // Catalog header level
          setOpenControl('');
          break;
        case 1: // Group at one level of depth
          setOpenControl(splitId.slice(0, -1).join(':'));
          break;
        case 2: // Group at 2+ levels of depth
        default:
          setOpenControl(`${splitId.slice(0, -1).join(':')}:${splitGroups.slice(0, -1).join('.')}`);
      }
    } else {
      setOpenControl(id);
    }
  };

  useEffect(() => {
    if (!initialized) {
      setDetails(initialDetails);
      setCheckedControls(initialState);
      setInitialized(true);
    }
  }, [initialized]);

  const handleChange = (newDetails) => {
    setDetails(newDetails);
  };

  const handleModalClose = () => {
    // no-op
  };

  const handleCancel = () => {
    setInitialized(false);
    handleClose();
  };

  const handleSave = () => {
    // { id: [id, type] }
    const selectedControls = Object.fromEntries(
      Object.entries(checkedControls)
        .filter(([k, v]) => v.value) // filter out unselected controls
        .map(([k, v]) => [k, [k, v.type]]),
    );
    const framework = {
      selectedControls,
      description: details.description,
      title: details.title,
    };
    dispatch(createCustomFramework(framework));
    setInitialized(false);
    handleClose();
  };

  return (
    <Modal open onClose={handleModalClose()}>
      <Container variant="modal" disableGutters>
        <Stack variant="modalTop" spacing={2}>
          <Typography variant="h6">{t('framework.createModal.title')}</Typography>
          <Details handleChange={handleChange} initDetails={details} />
          <FrameworkList
            initialized={initialized}
            catalogList={catalogList}
            checkedControls={checkedControls}
            openControl={openControl}
            handleCheckChange={handleCheckChange}
            handleControlOpen={handleControlOpen}
          />
        </Stack>
        <Stack variant="modalButtons" direction="row" justifyContent="flex-end" spacing={1}>
          <Button id='cancel' variant="text" startIcon={<ClearIcon />} onClick={() => handleCancel()}>
            {t('framework.createModal.buttonCancel')}
          </Button>
          <Button
            id='create'
            variant="contained"
            disabled={!details.title}
            startIcon={<SaveIcon />}
            onClick={() => handleSave()}
          >
            {t('framework.createModal.buttonSave')}
          </Button>
        </Stack>
      </Container>
    </Modal>
  );
}
